import clsx from "clsx";
import React, { FC, useEffect, useState } from "react";
import { Path, useFormContext } from "react-hook-form";

import { MaterialType } from "../../../types/materials";
import { Button } from "../../buttons/button";
import { Chip, Input } from "../../forms";
import { TagColors, TagDimensions } from "../../forms/elements/tags/types";
import {
  MaterialOption,
  MaterialsFormFields,
  MaterialsFormFieldsLabels,
  MaterialsFormFieldsProps,
  materialsOptions,
} from "../../forms/forms-fields/materials-form-fields";
import { Icon } from "../../icon";
import { FormPayload } from "./types";

export interface CustomerStoriesFilterProps {
  onSubmit?: () => void;
  labels: Record<string, string | null>;
  submittedFormPayload: FormPayload;
}

export const CustomerStoriesFilter: FC<CustomerStoriesFilterProps> = ({
  onSubmit,
  labels,
  submittedFormPayload,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { register, reset, setValue } = useFormContext<FormPayload>();

  useEffect(() => {
    if (!isOpen) {
      reset(submittedFormPayload);
    }
  }, [isOpen, reset, submittedFormPayload]);

  return (
    <div className="w-full">
      <Button
        type="button"
        size="sm"
        variant="primary"
        onClick={() => {
          setIsOpen((x) => !x);
        }}
        className="flex gap-1 items-center"
      >
        {labels["customer-stories-filter"]}
        <Icon icon="filter" className="text-light w-5 pt-1/2" />
      </Button>
      <div
        className={clsx(
          "transition-all duration-500 overflow-hidden",
          isOpen && "max-h-screen",
          !isOpen && "max-h-0"
        )}
      >
        <div className="mt-6 flex flex-col flex-wrap gap-6 px-5 py-6 rounded-3xl bg-light relative overflow-auto items-stretch w-full">
          <Icon icon="close" onClick={() => setIsOpen(false)} className="absolute top-4 right-4" />
          <div className="flex flex-col lg:flex-row flex-wrap gap-6 items-bottom">
            <MaterialsFormFields
              labels={labels as unknown as MaterialsFormFieldsProps["labels"]}
              materialsToShow={Object.values(MaterialType) as MaterialOption[]}
            />
            <div className="text-[15px] font-bold leading-tight flex flex-col gap-3 flex-grow">
              <Input
                {...register?.("nameSearch")}
                label={labels["customer-stories-filter-name"]}
                collapseMessage
              />
            </div>
            <div className="text-[15px] font-bold leading-tight flex flex-col gap-3 flex-grow">
              <Input
                {...register?.("descriptionSearch")}
                label={labels["customer-stories-filter-description"]}
                collapseMessage
              />
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            className="self-end"
            onClick={() => {
              onSubmit?.();
              setIsOpen(false);
            }}
          >
            {labels["customer-stories-filter-apply"]}
          </Button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {materialsOptions
          .filter((key) => submittedFormPayload.materials[key])
          .map((key) => (
            <Chip
              checked
              dimension={TagDimensions.Medium}
              onChange={() => {
                setValue(`materials.${key}` as Path<FormPayload>, false);
                onSubmit?.();
                setIsOpen(false);
              }}
              key={key}
              color={TagColors.Neutral}
            >
              {labels?.[key as keyof MaterialsFormFieldsLabels]}
            </Chip>
          ))}
        {submittedFormPayload.nameSearch && (
          <Chip
            checked
            dimension={TagDimensions.Medium}
            onChange={() => {
              setValue(`nameSearch` as Path<FormPayload>, "");
              onSubmit?.();
              setIsOpen(false);
            }}
            key="name"
            color={TagColors.Neutral}
          >
            name: {submittedFormPayload.nameSearch}
          </Chip>
        )}
        {submittedFormPayload.descriptionSearch && (
          <Chip
            checked
            dimension={TagDimensions.Medium}
            onChange={() => {
              setValue(`descriptionSearch` as Path<FormPayload>, "");
              onSubmit?.();
              setIsOpen(false);
            }}
            key="description"
            color={TagColors.Neutral}
          >
            description: {submittedFormPayload.descriptionSearch}
          </Chip>
        )}
      </div>
    </div>
  );
};
